import React from 'react';
import { X, Eye, MousePointerClick, TrendingUp, Calendar, Target } from 'lucide-react';
import { Advertisement, AdvertisementAnalytics } from '../../types';

interface AdvertisementAnalyticsModalProps {
  advertisement: Advertisement;
  analytics: AdvertisementAnalytics[];
  onClose: () => void;
}

export default function AdvertisementAnalyticsModal({
  advertisement,
  analytics,
  onClose
}: AdvertisementAnalyticsModalProps) {
  // Get the first analytics record (should only be one per advertisement)
  const analyticsData = analytics[0] || {
    id: advertisement.id,
    title: advertisement.title,
    ad_type: advertisement.ad_type,
    impressions: advertisement.impressions,
    clicks: advertisement.clicks,
    ctr: advertisement.impressions > 0 ? (advertisement.clicks / advertisement.impressions) * 100 : 0,
    is_active: advertisement.is_active,
    priority: advertisement.priority,
    created_at: advertisement.created_at,
    updated_at: advertisement.updated_at
  };

  // Format date
  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Calculate engagement rate
  const engagementRate = analyticsData.impressions > 0
    ? ((analyticsData.clicks / analyticsData.impressions) * 100).toFixed(2)
    : '0.00';

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50"
      onClick={onClose}
      data-testid="advertisement-analytics-modal"
    >
      <div
        className="bg-white rounded-lg max-w-3xl w-full max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">Advertisement Analytics</h2>
            <p className="text-sm text-gray-600 mt-1">{analyticsData.title}</p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
            aria-label="Close modal"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Key Metrics */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* Impressions */}
            <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg p-6 border border-primary-light">
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-sm font-medium text-blue-900">Total Impressions</h3>
                <Eye className="h-5 w-5 text-primary" />
              </div>
              <p
                className="text-3xl font-bold text-blue-900"
                data-testid="analytics-impressions"
              >
                {analyticsData.impressions.toLocaleString()}
              </p>
              <p className="text-xs text-primary-dark mt-1">
                Times your ad was displayed
              </p>
            </div>

            {/* Clicks */}
            <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-lg p-6 border border-green-200">
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-sm font-medium text-green-900">Total Clicks</h3>
                <MousePointerClick className="h-5 w-5 text-green-600" />
              </div>
              <p
                className="text-3xl font-bold text-green-900"
                data-testid="analytics-clicks"
              >
                {analyticsData.clicks.toLocaleString()}
              </p>
              <p className="text-xs text-green-700 mt-1">
                Times users clicked your ad
              </p>
            </div>

            {/* CTR */}
            <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-lg p-6 border border-purple-200">
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-sm font-medium text-purple-900">Click-Through Rate</h3>
                <TrendingUp className="h-5 w-5 text-purple-600" />
              </div>
              <p
                className="text-3xl font-bold text-purple-900"
                data-testid="analytics-ctr"
              >
                {engagementRate}%
              </p>
              <p className="text-xs text-purple-700 mt-1">
                Percentage of clicks per impression
              </p>
            </div>
          </div>

          {/* Performance Insights */}
          <div className="bg-gray-50 rounded-lg p-6 border border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
              <Target className="h-5 w-5 mr-2 text-gray-600" />
              Performance Insights
            </h3>

            <div className="space-y-3">
              {/* CTR Benchmark */}
              <div className="flex items-start">
                <div className="flex-shrink-0 w-2 h-2 mt-2 rounded-full bg-primary"></div>
                <div className="ml-3 flex-1">
                  <p className="text-sm font-medium text-gray-900">CTR Performance</p>
                  <p className="text-sm text-gray-600">
                    {parseFloat(engagementRate) >= 2 ? (
                      <>
                        <span className="text-green-600 font-semibold">Excellent!</span> Your ad is performing above the industry average (typically 1-2%).
                      </>
                    ) : parseFloat(engagementRate) >= 1 ? (
                      <>
                        <span className="text-primary font-semibold">Good!</span> Your ad is performing at the industry average.
                      </>
                    ) : parseFloat(engagementRate) > 0 ? (
                      <>
                        <span className="text-yellow-600 font-semibold">Moderate.</span> Consider optimizing your ad content to improve engagement.
                      </>
                    ) : (
                      <>
                        <span className="text-gray-600 font-semibold">New Ad.</span> Your ad is still building impressions and clicks.
                      </>
                    )}
                  </p>
                </div>
              </div>

              {/* Engagement Level */}
              <div className="flex items-start">
                <div className="flex-shrink-0 w-2 h-2 mt-2 rounded-full bg-green-500"></div>
                <div className="ml-3 flex-1">
                  <p className="text-sm font-medium text-gray-900">Engagement Level</p>
                  <p className="text-sm text-gray-600">
                    {analyticsData.clicks > 100 ? (
                      'High engagement with over 100 clicks recorded.'
                    ) : analyticsData.clicks > 50 ? (
                      'Moderate engagement with steady click activity.'
                    ) : analyticsData.clicks > 0 ? (
                      'Growing engagement as users discover your ad.'
                    ) : (
                      'New ad - engagement data will appear as users interact.'
                    )}
                  </p>
                </div>
              </div>

              {/* Visibility */}
              <div className="flex items-start">
                <div className="flex-shrink-0 w-2 h-2 mt-2 rounded-full bg-purple-500"></div>
                <div className="ml-3 flex-1">
                  <p className="text-sm font-medium text-gray-900">Visibility</p>
                  <p className="text-sm text-gray-600">
                    {analyticsData.impressions > 1000 ? (
                      'Excellent visibility with over 1,000 impressions.'
                    ) : analyticsData.impressions > 500 ? (
                      'Good visibility with steady impression growth.'
                    ) : analyticsData.impressions > 0 ? (
                      'Building visibility as more users browse the platform.'
                    ) : (
                      'New ad - impressions will increase as it is displayed to users.'
                    )}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Advertisement Details */}
          <div className="bg-white rounded-lg p-6 border border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
              <Calendar className="h-5 w-5 mr-2 text-gray-600" />
              Advertisement Details
            </h3>

            <dl className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <dt className="text-sm font-medium text-gray-500">Ad Type</dt>
                <dd className="mt-1 text-sm text-gray-900 font-semibold">
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-primary-dark">
                    {analyticsData.ad_type}
                  </span>
                </dd>
              </div>

              <div>
                <dt className="text-sm font-medium text-gray-500">Priority Level</dt>
                <dd className="mt-1 text-sm text-gray-900 font-semibold">{analyticsData.priority}</dd>
              </div>

              <div>
                <dt className="text-sm font-medium text-gray-500">Status</dt>
                <dd className="mt-1 text-sm font-semibold">
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                    analyticsData.is_active
                      ? 'bg-green-100 text-green-800'
                      : 'bg-red-100 text-red-800'
                  }`}>
                    {analyticsData.is_active ? 'Active' : 'Inactive'}
                  </span>
                </dd>
              </div>

              <div>
                <dt className="text-sm font-medium text-gray-500">Created</dt>
                <dd className="mt-1 text-sm text-gray-900">{formatDate(analyticsData.created_at)}</dd>
              </div>

              <div className="md:col-span-2">
                <dt className="text-sm font-medium text-gray-500">Last Updated</dt>
                <dd className="mt-1 text-sm text-gray-900">{formatDate(analyticsData.updated_at)}</dd>
              </div>
            </dl>
          </div>

          {/* Tips for Improvement */}
          {parseFloat(engagementRate) < 1 && analyticsData.impressions > 100 && (
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6">
              <h3 className="text-sm font-semibold text-yellow-900 mb-2">
                💡 Tips to Improve Performance
              </h3>
              <ul className="text-sm text-yellow-800 space-y-1 list-disc list-inside">
                <li>Consider updating your ad title to be more compelling</li>
                <li>Make sure your image is eye-catching and relevant</li>
                <li>Test different display text (call-to-action)</li>
                <li>Verify your target URL is working correctly</li>
              </ul>
            </div>
          )}

          {/* Close Button */}
          <div className="flex justify-end pt-4 border-t border-gray-200">
            <button
              onClick={onClose}
              className="px-6 py-2 border border-gray-300 text-sm font-medium rounded-lg text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
